import { Component, Inject, OnInit } from '@angular/core';
import { ReferenceService } from '../../services/reference.service';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SelectionModel } from '@angular/cdk/collections';
import { ToasterService } from '../../../core/toaster/toaster.service';
import { environment } from '../../../../../environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { ToastrModule } from 'ngx-toastr';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { DocumentHistoryComponent } from '../document-history/document-history.component';
import { forkJoin } from 'rxjs';
import { AuthenticationService } from '../../../authentication/state/authentication.service';
import { ReferenceDocumentInfoDialogComponent } from '../reference-document-info-dialog/reference-document-info-dialog.component';


@Component({
  selector: 'app-document-grid-dialog',
  templateUrl: './document-grid-dialog.component.html',
  styleUrls: ['./document-grid-dialog.component.scss'],
})
export class DocumentGridDialogComponent implements OnInit {

  displayedColumns: string[] = ['select', 'urls', 'comments', 'decision', 'recipient', 'action', 'status', 'upload', 'saveFile', 'visibility'];
  dataSource: any[] = [];
  selection = new SelectionModel<any>(true, []);
  noDocumentsFound: boolean = false;
  recipientGroups = {
    start: [] as any[],
    submittedToReview: [] as any[],
    requestCompleted: [] as any[]
  };

  isDisabled: boolean = false;
  constructor(@Inject(MAT_DIALOG_DATA) public data: { formId: number, canEdit: boolean, canView: boolean, canDelete: boolean, admin: boolean, reviewer: boolean }, private referenceService: ReferenceService,
    public dialogRef: MatDialogRef<DocumentGridDialogComponent>, private toasterService: ToasterService, private translocoService: TranslocoService, private dialog: MatDialog, private authenticationService: AuthenticationService) { }

  ngOnInit(): void {
    this.loadRecipientGroups();
    this.loadDocuments();
  }

  private loadDocuments(): void {
    this.referenceService.getReferenceDocuments(this.data.formId).subscribe({
      next: (res: any) => {
        const documents = res ?? [];
        const latestVersions = new Map<string, any>();

        documents.forEach((doc: any) => {
          if (!doc.deletedInd) {
            const majorVersion = Math.floor(doc.versionNumber);
            const key = `${doc.documentId}_${majorVersion}`;
            const existingDoc = latestVersions.get(key);

            if (!existingDoc || doc.versionNumber > existingDoc.versionNumber) {
              latestVersions.set(key, doc);
            }
          }
        });

        this.dataSource = Array.from(latestVersions.values()).map((doc: any) => ({
          id: doc.documentId,
          urls: doc.urls,
          dateCreated: doc.dateCreated ?? 'N/A',
          uploadedBy: doc.uploadedBy,
          createdBy: doc.createdBy || 'N/A',
          status:
            doc.status === 'Approve'
              ? 'Approved'
              : doc.status === 'Reject'
                ? 'Rejected'
                : doc.status || 'Under review',
          documentVersionId: doc.documentVersionId,
          fileType: doc.fileType || 'N/A',
          fileName: doc.fileName || 'N/A',
          versionNumber: doc.versionNumber,
          stateName: doc.stateName,
          actionType: doc.actionType,
          isDisabled: false,
          senderID: doc.senderID,
          recipientID: doc.recipientID,
          uploadedDate: doc.uploadedDate
        }));

        this.dataSource.sort((a, b) => b.versionNumber - a.versionNumber);
        this.applyDisableCondition();

        this.noDocumentsFound = this.dataSource.length === 0;
      },
      error: (err) => {
        console.error('Error loading documents:', err);
        this.noDocumentsFound = true;
      }
    });
  }

  private applyDisableCondition(): void {
    if (!this.dataSource || this.dataSource.length === 0) return;

    const isReviewer = this.data.reviewer;
    const loggedInUser = this.logedInUserName;
    const isAdmin = this.data.admin;

    this.dataSource.forEach(element => {
      // Default: disabled
      element.isDisabled = true;

      // Admin can always edit
      if (isAdmin) {
        element.isDisabled = false;
        return;
      }
      element.isDisabled = true;

      // Logged-in user can edit if record is in Start state and uploaded by them
      if (element.stateName === 'Start' && element.uploadedBy === loggedInUser) {
        element.isDisabled = false;
        return;
      }

      if (
        element.stateName === 'SubmittedToReview' &&
        (isReviewer && element.recipientID === loggedInUser)
      ) {
        element.isDisabled = false;
        return;
      }

      if (element.stateName === 'RequestCompleted' ||
        (element.stateName === 'Start' && element.uploadedBy !== loggedInUser)) {
        element.isDisabled = true;
        return;
      }
    });

    this.isDisabled = this.dataSource.every(e => e.isDisabled === true);
  }


  closeDialog() {
    const allApproved = this.dataSource.length > 0 &&
      this.dataSource.every(doc =>
        doc.status === 'Approve' || doc.status === 'Approved'
      );

    const allRejected = this.dataSource.length > 0 &&
      this.dataSource.every(doc =>
        doc.status === 'Reject' || doc.status === 'Rejected'
      );

    let overallStatus: 'Approved' | 'Rejected' | 'Under review' | 'Pending' = 'Pending';

    if (allApproved) {
      overallStatus = 'Approved';
    } else if (allRejected) {
      overallStatus = 'Rejected';
    } else if (this.dataSource.length > 0) {
      overallStatus = 'Under review';
    }

    this.dialogRef.close(overallStatus);
  }


  isAllSelected() {
    const selectableRows = this.dataSource.filter(row => !row.isDisabled);
    return (
      selectableRows.length > 0 &&
      this.selection.selected.length === selectableRows.length
    );
  }

  masterToggle() {
    if (this.isDisabled) {
      return;
    }

    const selectableRows = this.dataSource.filter(row => !row.isDisabled);

    if (this.isAllSelected()) {
      selectableRows.forEach(row => this.selection.deselect(row));
    } else {
      selectableRows.forEach(row => this.selection.select(row));
    }
  }

  extractFileName(fullUrl: string): string {
    return fullUrl.split('/').pop() || fullUrl;
  }

  previewSelected(): void {
    const selectedDocs = this.selection.selected;

    if (!selectedDocs.length) {
      alert('Please select at least one document to preview.');
      return;
    }

    selectedDocs.forEach(doc => {
      window.open(doc.urls, '_blank');
    });
  }

  downloadSelected(): void {
    const selectedDocs = this.selection.selected;

    if (!selectedDocs.length) {
      alert('Please select at least one document to download.');
      return;
    }

    const downloadedFileNames: string[] = [];
    const createdBy = selectedDocs[0]?.createdBy
    selectedDocs.forEach(doc => {
      const relativeUrl = this.extractRelativePath(doc.urls);
      if (relativeUrl) {
        const fullUrl = `${environment.apiUrl}/${relativeUrl}`;
        this.downloadFile(fullUrl);
        downloadedFileNames.push(this.extractFileName(fullUrl));
      }
    });
    const payload = {
      createdBy: createdBy,
      DocumentNames: downloadedFileNames
    };

    const userId = this.referenceService.getUserId();
    this.referenceService.sendDownloadNotification(payload).subscribe({
      next: () => {
        this.toasterService.success(`User ${userId} downloaded: ${downloadedFileNames.join(', ')}`);
      },
      error: err => {
        console.error('Failed to send download notification:', err);
      }
    });
  }

  extractRelativePath(fullPath: string): string {
    const match = fullPath.match(/uploads\/.*$/);
    return match ? match[0] : '';
  }

  downloadFile(fileUrl: string): void {
    const link = document.createElement('a');
    link.href = fileUrl;
    link.setAttribute('download', '');
    link.setAttribute('target', '_blank');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  deletesectedfile(): void {
    const selectedDocs = this.selection.selected;
    let completed = 0;

    if (!selectedDocs.length) {
      alert('Please select at least one document to delete.');
      return;
    }

    selectedDocs.forEach(doc => {
      this.referenceService.deleteUploadedFile(doc.id, doc.versionNumber, doc.documentVersionId,).subscribe({
        next: () => {
          this.toasterService.success(`Document ${this.extractFileName(doc.urls)} deleted successfully.`);
          this.dataSource = this.dataSource.filter(d => d.id !== doc.id);
          this.selection.deselect(doc);
          completed++;
          if (completed === selectedDocs.length) {
            this.closeDialog();
          }
        },
        error: err => {
          console.error('Failed to delete document:', err);
          this.toasterService.error(`Failed to delete document ${this.extractFileName(doc.urls)}.`);
        }
      });
    });
  }

  saveRow(element: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: { message: 'Are you sure you want to approve this document?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.referenceService.updateStatus(element.versionNumber, element.documentVersionId, element.id, element.fileName, 'Approved').subscribe({
          next: () => {
            element.status = 'Approved',
              this.toasterService.success(`Document approved successfully!.`);
            this.loadDocuments();
          },
          error: (err) => console.error('Failed to update status:', err)
        });
      }
    });
  }

  cancelRow(element: any) {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      width: '350px',
      data: { message: 'Are you sure you want to reject this document?' }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.referenceService.updateStatus(element.versionNumber, element.documentVersionId, element.id, element.fileName, 'Rejected').subscribe({
          next: () => {
            element.status = 'Rejected',
              this.toasterService.success(`Document rejected successfully!.`);
            this.loadDocuments();
          },
          error: (err) => console.error('Failed to update status:', err)
        });
      }
    });
  }


  onFileSelected(event: any, element: any): void {
    const files: FileList = event.target.files;

    if (!files || files.length === 0) return;

    if (!element.uploadedFiles) {
      element.uploadedFiles = [];
    }

    for (let i = 0; i < files.length; i++) {
      element.uploadedFiles.push(files[i]);
    }

    event.target.value = null;
  }

  saveFile(element: any): void {
    if (!element.uploadedFiles || element.uploadedFiles.length === 0) {
      this.toasterService.error("Please upload at least one file before saving!");
      return;
    }

    const formData = new FormData();

    const file = element.uploadedFiles[0];
    if (file) {
      formData.append('files', file, file.name);
    }

    formData.append('id', element.id.toString());
    formData.append('documentVersionId', element.documentVersionId.toString());
    formData.append('comments', element.comments || '');

    this.referenceService.reuploadDocument(formData).subscribe({
      next: () => {
        this.toasterService.success('File reuploaded successfully');

        element.uploadedFiles = element.uploadedFiles.map((f: any) => ({
          ...f,
          saved: true
        }));

        element.status = 'Uploaded';

        this.ngOnInit();
      },
      error: (err: any) => {
        console.error('Failed to upload file:', err);
        this.toasterService.error('Failed to reupload file');
      }
    });
  }

  removeUploadedFile(element: any, fileIndex: number): void {
    if (element.uploadedFiles && fileIndex > -1) {
      element.uploadedFiles.splice(fileIndex, 1);
    }
  }

  openHistory(element: any) {
    this.referenceService.getReferenceDocuments(element.documentVersionId, element.id, element.versionNumber).subscribe({
      next: (documents: any[]) => {
        const major = Math.floor(element.versionNumber);

        const documentVersions = documents
          .filter(doc =>
            Math.floor(doc.versionNumber) === major && doc.deletedInd === false
          )
          .sort((a, b) => b.versionNumber - a.versionNumber);

        this.dialog.open(DocumentHistoryComponent, {
          width: '1300px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          data: {
            documentVersions
          }
        });
      },
      error: (err) => {
        console.error('Error fetching document history', err);
      }
    });
  }

  public get logedInUserName() {
    return this.authenticationService.authenticationQuery.givenName
  }

  onSubmit() {
    const selectedElements = this.selection.selected;

    if (!selectedElements || selectedElements.length === 0) {
      this.toasterService.error('No documents selected for submission');
      return;
    }


    selectedElements.forEach(element => {
      console.log('Submitting element:', element);

      let recipientId = element.recipientId;
      let recipientName: string | null = null;

      if (element.stateName == "SubmittedToReview") {
        if ((!element.status || element.status == "Under review") && element.decision != "NeedMoreInfo") {
          this.toasterService.warning('Need the document status before submit to next state');
          return;
        }
        if ((!element.status || element.status == "Under review") && element.decision == "SubmittedToReview") {
          this.toasterService.warning('Need the document status before submit to next state');
          return;
        }
        if (element.decision == null || element.decision == "" || element.decision == undefined) {
          this.toasterService.error('Select the decision');
          return;
        }

        if (element.recipientId == null || element.recipientId == "" || element.recipientId == undefined) {
          this.toasterService.error('Select the recipient');
          return;
        }
      }

      if (element.decision == null || element.decision == "" || element.decision == undefined) {
        this.toasterService.error('Select the decision');
        return;
      }

      if (element.recipientId == null || element.recipientId == "" || element.recipientId == undefined) {
        this.toasterService.error('Select the recipient');
        return;
      }


      if (element.decision !== 'NeedMoreInfo') {
        const recipient =
          this.recipientGroups.submittedToReview?.find(u => u.id === element.recipientId) ||
          this.recipientGroups.start?.find(u => u.id === element.recipientId) ||
          this.recipientGroups.requestCompleted?.find(u => u.id === element.recipientId);

        recipientName = recipient ? recipient.userName : null;
      }
      else {
        recipientName = element.uploadedBy;
        recipientId = element.uploadedBy;
      }

      const payload = {
        documentId: element.id,
        stateName: element.stateName,
        recipientId: element.recipientId,
        decision: element.decision,
        comments: element.comments,
        uploadedBy: element.uploadedBy,
        dateCreated: element.dateCreated,
        documentVersionNumber: element.versionNumber,
        fileName: element.fileName,
        status: element.status,
      };

      this.referenceService.submitDocument(payload).subscribe({
        next: (res: any) => {
          this.toasterService.success(`Record submitted successfully to ${element.recipientId}`);
          this.loadDocuments();
          element.decision = null;
          element.recipientId = null;
          element.comments = '';
          this.selection.deselect(element);
        },
        error: (err: any) => console.error('Error submitting:', err)
      });
    });
  }


  loadRecipientGroups() {
    forkJoin({
      start: this.referenceService.getStartRoleUsers(),
      submittedToReview: this.referenceService.getSubmittedToReviewUsers(),
      requestCompleted: this.referenceService.getRequestCompletedUsers()
    }).subscribe({
      next: (res: any) => {
        this.recipientGroups.start = res.start || [];
        this.recipientGroups.submittedToReview = res.submittedToReview || [];
        this.recipientGroups.requestCompleted = res.requestCompleted || [];
      },
      error: (err) => console.error('Error fetching recipient data', err)
    });
  }

  onDecisionChange(element: any, decision: string) {
    element.decision = decision;

    switch (decision) {
      case 'SubmittedToReview': {
        element.availableRecipients = this.recipientGroups.submittedToReview || [];
        break;
      }

      case 'RequestCompleted': {
        element.availableRecipients = this.recipientGroups.requestCompleted || [];
        element.recipientId = null;
        break;
      }

      case 'NeedMoreInfo': {
        const userName = element.uploadedBy;

        element.availableRecipients = [
          {
            id: userName,
            userName: userName
          }
        ];

        element.recipientId = null;
        break;
      }

      default: {
        element.availableRecipients = [];
      }
    }

    if (element.availableRecipients?.length === 1) {
      const singleRecipient = element.availableRecipients[0];
      element.recipientId = singleRecipient.userName;
      element.recipientDisabled = true;
    } else {
      element.recipientDisabled = false;
    }
  }

  openInfoDialog(element: any): void {
    this.dialog.open(ReferenceDocumentInfoDialogComponent, {
      width: '400px',
      data: element
    });

  }
}

